import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);     // null = checking, false = unauthenticated
  const [tenant, setTenant] = useState(null);
  const [ready, setReady] = useState(false);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem("pragma_token");
    if (!token) { setUser(false); setTenant(null); setReady(true); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setTenant(data.tenant || null);
    } catch {
      localStorage.removeItem("pragma_token");
      setUser(false);
      setTenant(null);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // 2FA required — no token yet. Caller will move to the code-entry step.
      if (data && data.mfa_required) {
        return { ok: true, mfaRequired: true, challengeId: data.challenge_id, emailHint: data.email_hint };
      }
      localStorage.setItem("pragma_token", data.token);
      setUser(data.user);
      setTenant(data.tenant || null);
      return { ok: true };
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || "Login failed";
      return { ok: false, error: detail, suspended: detail.toLowerCase().includes("suspended") };
    }
  };

  const verifyMfa = async (challengeId, code, rememberDevice) => {
    try {
      const { data } = await api.post("/auth/mfa/verify", {
        challenge_id: challengeId, code, remember_device: !!rememberDevice,
      });
      localStorage.setItem("pragma_token", data.token);
      setUser(data.user);
      setTenant(data.tenant || null);
      return { ok: true };
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || "Verification failed";
      return { ok: false, error: detail };
    }
  };

  const resendMfa = async (challengeId) => {
    try {
      await api.post("/auth/mfa/resend", { challenge_id: challengeId });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || "Could not resend" };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("pragma_token");
    setUser(false);
    setTenant(null);
  };

  const loginWithToken = async (token) => {
    localStorage.setItem("pragma_token", token);
    await fetchMe();
  };

  return (
    <AuthContext.Provider value={{ user, tenant, ready, login, logout, refresh: fetchMe, loginWithToken, verifyMfa, resendMfa }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
